import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, AutocompleteAPI, CheckboxAPI, DateFieldAPI, FieldAPI, FormAPI, InputAPI, LocatorAPI, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { Inject } from '@odinjs/odin';

import moment, { Moment } from 'moment';

import { FragmentNames, HealthHistoryAllergiesEditAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { Endpoint } from '../../../../constants/endpoints';
import { WizardSteps } from '../../../../constants/wizards';
import { AllergiesRadioGroupExpressions, AllergiesRadioGroupId } from '../radio-group-data/allergies-radio-group-expressions';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { DomainType, HealthHistoryAllergiesResponse, HealthHistoryType, SearchType } from '../../health-history-types';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import DialogService from '../../../../services/dialog-service';
import { YesNoType } from '../../../../utils/enum-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { DEFAULT_DATE_PATTERN, ISO_FULL_DATE_FORMAT, MONTH_YEAR_PATTERN, YEAR_PATTERN } from '../../../../constants/date-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { AllergiesDateHandleStartOf, AllergiesDateTypeId } from '../autocomplete-data/allergies-autocomplete-expressions';
import HealthHistoryAllergiesCommons from '../health-history-allergies-commons/health-history-allergies-commons';

@Controller({ component: HealthHistoryAllergiesEditAliases.LAYOUT_HEALTH_HISTORY_ALLERGIES, fragment: FragmentNames.HEALTH_HISTORY_ALLERGIES_EDIT })
export default class HealthHistoryAllergiesEditController extends BaseController {

  @Inject(HealthHistoryAllergiesEditAliases.ACTION_DELETE_HEALTH_HISTORY_ALLERGIES)
  private actionDeleteAPI!: ActionAPI;

  @Inject(HealthHistoryAllergiesEditAliases.ACTION_RELEASE_HEALTH_HISTORY_ALLERGIES)
  private actionReleaseAPI!: ActionAPI;

  @Inject(HealthHistoryAllergiesEditAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesEditAliases.RADIO_GROUP_CLASSIFICATION)
  private radioGroupClassificationAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesEditAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY)
  private radioGroupHighlightIntensitySeverityAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_CAUSATIVE_AGENT)
  private autoCompleteCausativeAgentAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FIELD_ACTIVE_INGREDIENT)
  private fieldActiveIngredientAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.LOCATOR_ACTIVE_INGREDIENT)
  private locatorActiveIngredientAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_ACTIVE_INGREDIENT)
  private inputActiveIngredientAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FIELD_DCB)
  private fieldDcbAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.LOCATOR_DCB)
  private locatorDcbAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_DCB)
  private inputDcbAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.LOCATOR_MATERIAL_MEDICINE)
  private locatorMaterialMedicineAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_MATERIAL_MEDICINE)
  private inputMaterialMedicineAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.LOCATOR_MATERIAL_MEDICINE_CLASS)
  private locatorMaterialMedicineClassAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_MATERIAL_MEDICINE_CLASS)
  private inputMaterialMedicineClassAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.LOCATOR_FAMILY)
  private locatorFamilyAPI!: LocatorAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_FAMILY)
  private inputFamilyAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_REACTION_TYPE)
  private autoCompleteReactionTypeAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesEditAliases.RADIO_GROUP_CONDITION)
  private radioGroupConditionAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_APPROACH)
  private autoCompleteApproachAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_DATE_TYPE)
  private  autoCompleteDateTypeAPI!: AutocompleteAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FIELD_START_DATE)
  private fieldStartDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.DATE_FIELD_START_DATE)
  private dateFieldStartDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FIELD_START_MONTH)
  private fieldStartMonthAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_START_MONTH)
  private inputStartMonthAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.FIELD_START_YEAR)
  private fieldStartYearAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_START_YEAR)
  private inputStartYearAPI!: InputAPI;

  @Inject(HealthHistoryAllergiesEditAliases.DATE_FIELD_END_DATE)
  private dateFieldEndDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.DATE_FIELD_LAST_DATE)
  private dateFieldLastDateAPI!: DateFieldAPI;

  @Inject(HealthHistoryAllergiesEditAliases.INPUT_UNREGISTERED_CAUSATIVE_AGENT)
  private readonly inputUnregisteredCausativeAgentAPI!: InputAPI;

  @OnMount({ target: HealthHistoryAllergiesEditAliases.WIZARD})
  handleMountWizard(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    InformationBarUtils.collapsedBar = true;
    HealthHistoryListContext.currentTab = HealthHistoryType.ALLERGIES
    this.handleFetchAllergy();
  }

  @OnMount({ target: HealthHistoryAllergiesEditAliases.FORM_ONE })
  handleMountForm(): void {
    applicationContext.popNavigationItem();
    this.formOneAPI.setLoading(true);
    this.actionDeleteAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
  }

  @OnChange(HealthHistoryAllergiesEditAliases.CHECKBOX_DENIES_ALLERGIES)
  handleChangeCheckboxDeniesAllergies(event: ChangeEvent<CheckboxAPI>): void {
    const isDeniesAllergies = event.value as unknown as boolean;
    this.context.set({ isDeniesAllergies });
    this.handleDeniesDisabledFields();
  }

  @OnChange(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_CAUSATIVE_AGENT)
  handleChangeAutoCompleteCausativeAgent(event: ChangeEvent<AutocompleteAPI>): void {
    const causativeAgent             = event.value as unknown as SearchType;
    this.handleCausativeAgentDisabledFields(causativeAgent);
  }

  @OnChange(HealthHistoryAllergiesEditAliases.LOCATOR_ACTIVE_INGREDIENT)
  handleChangeLocatorActiveIngredient(): void {
    this.handleDisabledFields(this.locatorActiveIngredientAPI.getAlias(), this.locatorActiveIngredientAPI.isEnabled(), !this.inputActiveIngredientAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesEditAliases.LOCATOR_DCB)
  handleChangeLocatorDcb(): void {
    const isUserLocalePtBr = HealthHistoryAllergiesCommons.handleUserLocale();

    if (isUserLocalePtBr) {
      this.handleDisabledFields(this.locatorDcbAPI.getAlias(), this.locatorDcbAPI.isEnabled(), !this.inputDcbAPI.getValue());
    }
  }

  @OnChange(HealthHistoryAllergiesEditAliases.LOCATOR_MATERIAL_MEDICINE)
  handleChangeLocatorMaterialMedicine(): void {
    this.handleDisabledFields(this.locatorMaterialMedicineAPI.getAlias(), this.locatorMaterialMedicineAPI.isEnabled(), !this.inputMaterialMedicineAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesEditAliases.LOCATOR_MATERIAL_MEDICINE_CLASS)
  handleChangeLocatorMaterialMedicineClass(): void {
    this.handleDisabledFields(this.locatorMaterialMedicineClassAPI.getAlias(), this.locatorMaterialMedicineClassAPI.isEnabled(), !this.inputMaterialMedicineClassAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesEditAliases.LOCATOR_FAMILY)
  handleChangeLocatorFamily(): void {
    this.handleDisabledFields(this.locatorFamilyAPI.getAlias(), this.locatorFamilyAPI.isEnabled(), !this.inputFamilyAPI.getValue());
  }

  @OnChange(HealthHistoryAllergiesEditAliases.INPUT_UNREGISTERED_CAUSATIVE_AGENT)
  handleBlurUnregisteredCausativeAgent(event: ChangeEvent<InputAPI>): void {
    this.handleDisabledFields(this.inputUnregisteredCausativeAgentAPI.getAlias(), this.inputUnregisteredCausativeAgentAPI.isEnabled(), !event.value);
  }

  @OnChange(HealthHistoryAllergiesEditAliases.AUTOCOMPLETE_DATE_TYPE)
  handleChangeAutoCompleteDateType(event: ChangeEvent<DomainType>): void{
    const dateType = event.value;
    const oldDateType = event?.oldValue;
    this.handleDateTypeFields(dateType, oldDateType);
  }

  @OnValidate(HealthHistoryAllergiesEditAliases.FORM_ONE)
  handleValidateFormOne(validationFormOne: ValidateEvent): void {
    this.handleStepDone(validationFormOne.valid, WizardSteps.STEP_ONE);
  }

  @OnValidate(HealthHistoryAllergiesEditAliases.FORM_TWO)
  handleValidateFormTwo(validationFormTwo: ValidateEvent): void {
    this.handleStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO);
  }

  @OnValidate(HealthHistoryAllergiesEditAliases.FORM_THREE)
  handleValidateFormThree(validationFormThree: ValidateEvent): void {
    this.handleStepDone(validationFormThree.valid, WizardSteps.STEP_THREE);
  }

  @OnAfterNavigate(HealthHistoryAllergiesEditAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  @OnClick({ target: HealthHistoryAllergiesEditAliases.ACTION_DELETE_HEALTH_HISTORY_ALLERGIES })
  handleClickDeleteAllergy(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.handleConfirmDelete(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistoryAllergiesEditAliases.ACTION_RELEASE_HEALTH_HISTORY_ALLERGIES })
  handleClickReleaseAllergy(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistoryAllergiesEditAliases.ACTION_NEW_HEALTH_HISTORY_ALLERGIES })
  handleClickNewAllergy(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_ALLERGIES_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnBeforeSubmit({ target: HealthHistoryAllergiesEditAliases.WIZARD })
  handleBeforeSubmitWizard(): void {
    const { encounterId, idHealthHistory, patientId } = this.historyService.getCurrentRouteParams();
    const user                     = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formDataOne              = this.formOneAPI.getRecord();
    const formDataTwo              = this.formTwoAPI.getRecord();
    const formDataThree            = this.formThreeAPI.getRecord();
    const isFieldStartDateVisible  = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible  = this.fieldStartYearAPI.isVisible();

    const body = {
      activeIngredient: (formDataOne.activeIngredient as SearchType)?.id,
      approach: (formDataThree.approach as DomainType)?.valueDomain,
      attendance: encounterId as number,
      causativeAgent: (formDataOne.causativeAgent as SearchType)?.id,
      classification: (formDataOne.classification as SelectableItem)?.key,
      condition: (formDataTwo.condition as SelectableItem)?.key,
      dateType: (formDataThree.dateType as DomainType)?.valueDomain,
      dcb: (formDataTwo.dcb as SearchType)?.id,
      deniesAllergies: BooleanUtils.isPositiveValue(formDataOne.deniesAllergies as boolean),
      endDate: formDataThree.endDate ? moment(String(formDataThree.endDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      family: (formDataTwo.family as SearchType)?.id,
      highlightIntensitySeverity: (formDataOne.highlightIntensitySeverity as SelectableItem)?.key,
      idHealthHistory,
      lastDate: formDataThree.lastDate ? moment(String(formDataThree.lastDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      material: (formDataTwo.material as SearchType)?.id,
      materialClass: (formDataTwo.materialClass as SearchType)?.id,
      notes: formDataThree.notes,
      origin: (formDataOne.origin as DomainType)?.valueDomain,
      patientAlerts: BooleanUtils.isPositiveValue(formDataOne.patientAlerts as boolean),
      physicalPerson: patientId,
      problemsList: BooleanUtils.isPositiveValue(formDataThree.problemsList as boolean),
      professional: user.physicalPersonId,
      reactionType: (formDataTwo.reactionType as SearchType)?.id,
      recordDate: moment(String(formDataOne.recordDate)).utc().format(ISO_FULL_DATE_FORMAT),
      securityLevel: (formDataThree.securityLevel as SearchType)?.id,
      startDate: (isFieldStartDateVisible && formDataThree.startDate) ? moment(String(formDataThree.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      startMonth: (isFieldStartMonthVisible && formDataThree.startMonth) ? moment(String(formDataThree.startMonth)).startOf('month').utc().format(ISO_FULL_DATE_FORMAT) : undefined,
      startYear: (isFieldStartYearVisible && formDataThree.startYear) ? HealthHistoryAllergiesCommons.getOnlyYear(formDataThree.startYear) : undefined,
      unregisteredCausativeAgent: formDataTwo.unregisteredCausativeAgent
    } as Record<string, Value>;

    this.handleEditAllergy(body);
  }

  private handleDeniesDisabledFields(): void {
    const isDeniesAllergies        = this.context.get('isDeniesAllergies') as boolean;
    const isFieldStartDateVisible  = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible  = this.fieldStartYearAPI.isVisible();
    const isUserLocalePtBr         = HealthHistoryAllergiesCommons.handleUserLocale();

    this.radioGroupHighlightIntensitySeverityAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteCausativeAgentAPI.setEnabled(!isDeniesAllergies);
    this.locatorActiveIngredientAPI.setEnabled(!isDeniesAllergies);
    this.inputActiveIngredientAPI.setEnabled(!isDeniesAllergies);
    this.locatorMaterialMedicineAPI.setEnabled(!isDeniesAllergies);
    this.inputMaterialMedicineAPI.setEnabled(!isDeniesAllergies);
    this.locatorMaterialMedicineClassAPI.setEnabled(!isDeniesAllergies);
    this.inputMaterialMedicineClassAPI.setEnabled(!isDeniesAllergies);
    this.locatorFamilyAPI.setEnabled(!isDeniesAllergies);
    this.inputFamilyAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteReactionTypeAPI.setEnabled(!isDeniesAllergies);
    this.radioGroupConditionAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteApproachAPI.setEnabled(!isDeniesAllergies);
    this.autoCompleteDateTypeAPI.setEnabled(!isDeniesAllergies);
    this.dateFieldEndDateAPI.setEnabled(!isDeniesAllergies);
    this.dateFieldLastDateAPI.setEnabled(!isDeniesAllergies);
    this.inputUnregisteredCausativeAgentAPI.setEnabled(!isDeniesAllergies);

    if (isUserLocalePtBr) {
      this.locatorDcbAPI.setEnabled(!isDeniesAllergies);
      this.inputDcbAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setEnabled(!isDeniesAllergies);
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setEnabled(!isDeniesAllergies);
    }

    if (isDeniesAllergies) {
      this.handleDeniesEmptyFields();
    }

    this.handleMedicineRequiredFields();
  }

  private handleDeniesEmptyFields(): void {
    const emptyData                = {} as Record<string, Value> ;
    const isFieldStartDateVisible  = this.fieldStartDateAPI.isVisible();
    const isFieldStartMonthVisible = this.fieldStartMonthAPI.isVisible();
    const isFieldStartYearVisible  = this.fieldStartYearAPI.isVisible();
    const isUserLocalePtBr         = HealthHistoryAllergiesCommons.handleUserLocale();

    this.radioGroupHighlightIntensitySeverityAPI.setValue(this.radioGroupHighlightIntensitySeverityAPI.getData()[3]);
    this.autoCompleteCausativeAgentAPI.setValue(undefined);
    this.autoCompleteReactionTypeAPI.setValue(undefined);
    this.locatorActiveIngredientAPI.setValue(emptyData);
    this.locatorMaterialMedicineAPI.setValue(emptyData);
    this.locatorMaterialMedicineClassAPI.setValue(emptyData);
    this.locatorFamilyAPI.setValue(emptyData);
    this.radioGroupConditionAPI.setValue(this.radioGroupConditionAPI.getData()[0]);
    this.autoCompleteApproachAPI.setValue(undefined);
    this.autoCompleteDateTypeAPI.setValue(undefined);
    this.dateFieldEndDateAPI.setValue('');
    this.dateFieldLastDateAPI.setValue('');
    this.inputUnregisteredCausativeAgentAPI.setValue('');

    if (isUserLocalePtBr) {
      this.locatorDcbAPI.setValue(emptyData);
    }

    if (isFieldStartDateVisible) {
      this.dateFieldStartDateAPI.setValue('');
    }

    if (isFieldStartMonthVisible) {
      this.inputStartMonthAPI.setValue('');
    }

    if (isFieldStartYearVisible) {
      this.inputStartYearAPI.setValue('');
    }
  }

  private handleCausativeAgentDisabledFields(causativeAgent: SearchType): void {
    const isCausativeAgentEnabled          = this.autoCompleteCausativeAgentAPI.isEnabled();
    const isMedicine                       = (causativeAgent?.medicine ?? YesNoType.YES) === YesNoType.YES;
    const isUnregistered                   = (causativeAgent?.unregistered ?? YesNoType.YES) === YesNoType.YES;
    const isActiveIngredientRequired       = (causativeAgent?.activeIngredientRequired ?? YesNoType.NO) === YesNoType.YES;
    const isDcbRequired                    = (causativeAgent?.dcbRequired ?? YesNoType.NO) === YesNoType.YES;
    const isDeniesAllergies                = this.context.get('isDeniesAllergies') as boolean;
    const locatorEmptyData                 = {} as Record<string, Value> ;
    const inputEmptyData                   = '';
    const enableDeniesAllergiesMedicine    = !isDeniesAllergies && isMedicine;
    const enableUnregisteredCausativeAgent = !isDeniesAllergies && isUnregistered;
    const isUserLocalePtBr                 = HealthHistoryAllergiesCommons.handleUserLocale();

    this.context.set({ isActiveIngredientRequired, isDcbRequired, isMedicine, isUnregistered });

    if (isCausativeAgentEnabled) {
      this.locatorActiveIngredientAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputActiveIngredientAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorMaterialMedicineAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputMaterialMedicineAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorMaterialMedicineClassAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputMaterialMedicineClassAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.locatorFamilyAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputFamilyAPI.setEnabled(enableDeniesAllergiesMedicine);
      this.inputUnregisteredCausativeAgentAPI.setEnabled(enableUnregisteredCausativeAgent);

      this.locatorActiveIngredientAPI.setValue(locatorEmptyData);
      this.locatorMaterialMedicineAPI.setValue(locatorEmptyData);
      this.locatorMaterialMedicineClassAPI.setValue(locatorEmptyData);
      this.locatorFamilyAPI.setValue(locatorEmptyData);
      this.inputUnregisteredCausativeAgentAPI.setValue(inputEmptyData);

      if (isUserLocalePtBr) {
        this.locatorDcbAPI.setEnabled(enableDeniesAllergiesMedicine);
        this.inputDcbAPI.setEnabled(enableDeniesAllergiesMedicine);
        this.locatorDcbAPI.setValue(locatorEmptyData);
      }

      this.handleMedicineRequiredFields();
    }
  }

  private handleDisabledFields(componentAlias: string, isEnabled: boolean, isValid: boolean): void {

    if (!isEnabled) {
      return;
    }

    const isMedicine         = this.context.get('isMedicine') as boolean;
    const isUnregistered     = this.context.get('isUnregistered') as boolean;
    const isMatch            = (alias: string): boolean => componentAlias === alias || (isValid && isMedicine);
    const enableUnregistered = componentAlias === this.inputUnregisteredCausativeAgentAPI.getAlias() || (isValid && isUnregistered);

    this.locatorActiveIngredientAPI.setEnabled(isMatch(this.locatorActiveIngredientAPI.getAlias()));
    this.inputActiveIngredientAPI.setEnabled(isMatch(this.locatorActiveIngredientAPI.getAlias()));
    this.locatorMaterialMedicineAPI.setEnabled(isMatch(this.locatorMaterialMedicineAPI.getAlias()));
    this.inputMaterialMedicineAPI.setEnabled(isMatch(this.locatorMaterialMedicineAPI.getAlias()));
    this.locatorMaterialMedicineClassAPI.setEnabled(isMatch(this.locatorMaterialMedicineClassAPI.getAlias()));
    this.inputMaterialMedicineClassAPI.setEnabled(isMatch(this.locatorMaterialMedicineClassAPI.getAlias()));
    this.locatorFamilyAPI.setEnabled(isMatch(this.locatorFamilyAPI.getAlias()));
    this.inputFamilyAPI.setEnabled(isMatch(this.locatorFamilyAPI.getAlias()));
    this.inputUnregisteredCausativeAgentAPI.setEnabled(enableUnregistered);

    if (HealthHistoryAllergiesCommons.handleUserLocale()) {
      this.locatorDcbAPI.setEnabled(isMatch(this.locatorDcbAPI.getAlias()));
      this.inputDcbAPI.setEnabled(isMatch(this.locatorDcbAPI.getAlias()));
    }

    this.handleMedicineRequiredFields();
  }

  private handleMedicineRequiredFields(): void {
    const isActiveIngredientRequired  = this.context.get('isActiveIngredientRequired') as boolean;
    const isActiveIngredientEnabled   = this.locatorActiveIngredientAPI.isEnabled();
    const validator: Validator        = { name: 'required', options: {}};
    const isUserLocalePtBr            = HealthHistoryAllergiesCommons.handleUserLocale();

    this.fieldActiveIngredientAPI.removeValidator('required');

    if (isActiveIngredientRequired && isActiveIngredientEnabled) {
      this.fieldActiveIngredientAPI.addValidator(validator);
    }

    if (isUserLocalePtBr) {
      const isDcbRequired = this.context.get('isDcbRequired') as boolean;
      const isDcbEnabled  = this.locatorDcbAPI.isEnabled();

      this.fieldDcbAPI.removeValidator('required');

      if  (isDcbRequired && isDcbEnabled) {
        this.fieldDcbAPI.addValidator(validator);
      }
    }
  }

  private handleDateTypeFields(dateType: DomainType, oldDateType: DomainType | undefined): void {
    const oldValue = oldDateType ? this.getValueByDateType(oldDateType) : '';

    if (!dateType) {
      return
    }

    this.handleVisibilityDates(dateType);

    if (oldValue) {
      this.setOldValueInput(oldValue, dateType);
    }
  }

  private getValueByDateType(dateType: DomainType): Moment | undefined {
    let dateValue;
    switch(dateType.valueDomain) {
      case AllergiesDateTypeId.YEAR: {
        if (!this.inputStartYearAPI.getValue()) break;
        dateValue = moment().year(this.inputStartYearAPI.getValue() as number).startOf(AllergiesDateHandleStartOf.START_MONTH);
        break;
      }
      case AllergiesDateTypeId.MONTH_YEAR: {
        if (!this.inputStartMonthAPI.getValue()) break;
        dateValue = moment((this.inputStartMonthAPI.getValue() as string)).startOf(AllergiesDateHandleStartOf.START_MONTH);
        break;
      }
      default: {
        if (!this.dateFieldStartDateAPI.getValue()) break;
        dateValue = moment(this.dateFieldStartDateAPI.getValue() as string);
        break;
      }
    }

    return dateValue;
  }

  private handleVisibilityDates(dateType: DomainType | Record<string, Value>): void {
    const isDayMonthYear = dateType.valueDomain === AllergiesDateTypeId.DAY_MONTH_YEAR || !dateType?.valueDomain;
    const isMonthYear    = dateType.valueDomain === AllergiesDateTypeId.MONTH_YEAR;
    const isYear         = dateType.valueDomain === AllergiesDateTypeId.YEAR;

    this.fieldStartDateAPI.setVisible(isDayMonthYear);
    this.fieldStartMonthAPI.setVisible(isMonthYear);
    this.fieldStartYearAPI.setVisible(isYear);
  }

  private setOldValueInput(oldValue : Moment, dateType: DomainType): void  {
    switch(dateType.valueDomain) {
      case AllergiesDateTypeId.YEAR: {
        this.fieldStartYearAPI.setValue(moment(oldValue).startOf(AllergiesDateHandleStartOf.START_YEAR).format(YEAR_PATTERN));
        break;
      }
      case AllergiesDateTypeId.MONTH_YEAR: {
        this.fieldStartMonthAPI.setValue(moment(oldValue).startOf(AllergiesDateHandleStartOf.START_MONTH).format(MONTH_YEAR_PATTERN));
        break;
      }
      default: {
        this.fieldStartYearAPI.setValue(moment(oldValue).format(DEFAULT_DATE_PATTERN));
        break;
      }
    }
  }

  private handleStepDone(isStepDone: boolean, step: WizardSteps): void {
    if (isStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  private handleFetchAllergy(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();

    return HttpService.get(Endpoint.HEALTH_HISTORY_ALLERGIES, { sequenceNumberAllergy: idHealthHistory })
      .then(response => {
        const record = response.body as HealthHistoryAllergiesResponse;
        this.handleTreatData({ ...record });
        this.wizardAPI.setStepDone(WizardSteps.STEP_ONE, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      }).finally(() => {
        this.formOneAPI.setLoading(false);
      });
  }

  private handleTreatData(record: HealthHistoryAllergiesResponse): void {
    const deniesAllergies            = BooleanUtils.isTrueValue(record.deniesAllergies);
    const patientAlerts              = BooleanUtils.isTrueValue(record.patientAlerts);
    const problemsList               = BooleanUtils.isTrueValue(record.problemsList);
    const causativeAgent             = record.causativeAgent as SearchType;
    const unregisteredCausativeAgent = record.unregisteredCausativeAgent as string;
    const dateType                   = record.dateType as DomainType;
    const startDate                  = HealthHistoryAllergiesCommons.getFormattedDateOrUndefined(record.startDate);
    const startMonth                 = HealthHistoryAllergiesCommons.getFormattedMonthOrUndefined(record.startMonth);
    const endDate                    = HealthHistoryAllergiesCommons.getFormattedDateOrUndefined(record.endDate);
    const lastDate                   = HealthHistoryAllergiesCommons.getFormattedDateOrUndefined(record.lastDate);
    const allergyRecord              = {
      ...record,
      deniesAllergies,
      endDate,
      lastDate,
      patientAlerts,
      problemsList,
      startDate,
      startMonth };

    this.context.set({
      allergies: allergyRecord,
      classification: record.classification,
      condition: record.condition,
      highlightIntensitySeverity: record.highlightIntensitySeverity,
      isDeniesAllergies: deniesAllergies
    });

    const oldValueDate               = (record.startDate && startDate) ||
                                       (record.startYear && HealthHistoryAllergiesCommons.getFormattedYearOrUndefined(record.startYear)) ||
                                       (record.startMonth && startMonth);

    this.formOneAPI.setEditing(true);
    this.formOneAPI.validate();
    this.formOneAPI.setDisabled(true);

    this.formOneAPI.setData(allergyRecord as unknown as Record<string, HealthHistoryAllergiesResponse>);
    this.formTwoAPI.setData(allergyRecord as unknown as Record<string, HealthHistoryAllergiesResponse>);
    this.formThreeAPI.setData(allergyRecord as unknown as Record<string, HealthHistoryAllergiesResponse>);

    this.handleDateTypeFields(dateType, oldValueDate as unknown as DomainType);
    this.handleDeniesDisabledFields();
    this.handleCausativeAgentDisabledFields(causativeAgent);

    if (unregisteredCausativeAgent) {
      this.handleDisabledFields(this.inputUnregisteredCausativeAgentAPI.getAlias(), this.inputUnregisteredCausativeAgentAPI.isEnabled(), false);
    }

    this.handleRadioGroupData(AllergiesRadioGroupId.CLASSIFICATION, this.radioGroupClassificationAPI, AllergiesRadioGroupExpressions.CLASSIFICATION);
    this.handleRadioGroupData(AllergiesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, this.radioGroupHighlightIntensitySeverityAPI, AllergiesRadioGroupExpressions.HIGHLIGHT_INTENSITY_SEVERITY);
    this.handleRadioGroupData(AllergiesRadioGroupId.CONDITION, this.radioGroupConditionAPI, AllergiesRadioGroupExpressions.CONDITION);

    this.actionDeleteAPI.setEnabled(true);
    this.actionReleaseAPI.setEnabled(true);
  }

  private handleRadioGroupData(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);

        this.handleRadioGroupValues(radioGroup, responseBody, type);
      }).catch((error) => {
        error?.body && this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private handleRadioGroupValues(radioGroup: RadioGroupAPI, responseBody: SelectableItem[], type: string): void {
    const classification             = this.context.get('classification');
    const highlightIntensitySeverity = this.context.get('highlightIntensitySeverity');
    const condition                  = this.context.get('condition');

    let radioGroupItem: SelectableItem;

    switch (type) {
      case AllergiesRadioGroupExpressions.HIGHLIGHT_INTENSITY_SEVERITY:
        radioGroupItem = responseBody.find((field) => field.key === highlightIntensitySeverity) as SelectableItem;
        this.formOneAPI.setData({ ...this.formOneAPI.getData(), [type]:radioGroupItem });
        break;
      case AllergiesRadioGroupExpressions.CONDITION:
        radioGroupItem = responseBody.find((field) => field.key === condition) as SelectableItem;
        this.formTwoAPI.setData({ ...this.formOneAPI.getData(), [type]:radioGroupItem });
        break;
      default:
        radioGroupItem = responseBody.find((field) => field.key === classification) as SelectableItem;
        this.formOneAPI.setData({ ...this.formOneAPI.getData(), [type]:radioGroupItem });
        break;
    }

    if (radioGroupItem) {
      radioGroup.setValue(radioGroupItem);
    }
  }

  private handleEditAllergy(body: Record<string, Value>): void {
    this.formOneAPI.setLoading(true);
    HttpService.post(Endpoint.POST_HEALTH_HISTORY_ALLERGIES, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistoryAllergiesResponse;
        this.showModalSubmit(idHealthHistory as number);
      })
      .catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formOneAPI.setLoading(false));
  }

  private handleDelete(idHealthHistory: number): void {
    this.formOneAPI.setLoading(true);

    HttpService.delete(Endpoint.HEALTH_HISTORY_ALLERGIES, { sequenceNumberAllergy: idHealthHistory })
      .then(() => {
        this.formOneAPI.setLoading(false);
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneAPI.setLoading(false)
      });
  }

  private handleConfirmDelete(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => {this.handleDelete(idHealthHistory);},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private handleReleaseAllergy(idHealthHistory: number): void {
    this.formOneAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_ALLERGIES, { sequenceNumberAllergy: idHealthHistory })
      .then(() => {
        this.formOneAPI.setLoading(false);
        applicationContext.popNavigationItem();
      }).catch((err) => {
        err?.body && this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneAPI.setLoading(false)
      });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.handleReleaseAllergy(idHealthHistory) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-allergies-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
